import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendingUp, BarChart3, Globe, Gauge, Loader2, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

function StatCard({ title, value, subtitle, trend }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs mt-1 ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
            {trend > 0 ? <ArrowUp className="h-3 w-3" /> : trend < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(trend)}% WoW
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function IntelligencePage() {
  const { t } = useTranslation();
  const { data: demand, isLoading: demandLoading } = useQuery({
    queryKey: ['intelligence-demand'],
    queryFn: () => api.get('/intelligence/demand?period=3m').then(r => r.data),
    retry: false,
  });

  const { data: supply } = useQuery({
    queryKey: ['intelligence-supply'],
    queryFn: () => api.get('/intelligence/supply').then(r => r.data),
    retry: false,
  });

  const { data: benchmarks } = useQuery({
    queryKey: ['intelligence-benchmarks'],
    queryFn: () => api.get('/intelligence/benchmarks').then(r => r.data),
    retry: false,
  });

  const { data: velocity } = useQuery({
    queryKey: ['intelligence-velocity'],
    queryFn: () => api.get('/intelligence/velocity').then(r => r.data),
    retry: false,
  });

  if (demandLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t("intelligence.marketIntelligence")}</h1>
        <p className="text-muted-foreground">{t("intelligence.anonymizedTradeDataAnd")}</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total RFQs (90 days)" value={demand?.total_rfqs || 0} trend={demand?.week_over_week?.change_percent} />
        <StatCard title="Active Suppliers" value={supply?.total_suppliers || 0} />
        <StatCard title="Avg Days to Close" value={velocity?.avg_days_to_close || '—'} subtitle={velocity?.total_closed_deals ? `${velocity.total_closed_deals} deals closed` : 'No data yet'} />
        <StatCard title="Your Deals (90d)" value={benchmarks?.your_stats?.deals_90d || 0} subtitle={`Platform avg: ${benchmarks?.platform_averages?.deals_per_org_90d || 0}`} />
      </div>

      <Tabs defaultValue="demand">
        <TabsList>
          <TabsTrigger value="demand" className="gap-1"><TrendingUp className="h-4 w-4" /> Demand</TabsTrigger>
          <TabsTrigger value="supply" className="gap-1"><Globe className="h-4 w-4" /> Supply</TabsTrigger>
          <TabsTrigger value="benchmarks" className="gap-1"><Gauge className="h-4 w-4" /> Benchmarks</TabsTrigger>
        </TabsList>

        {/* Demand Signals */}
        <TabsContent value="demand" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("intelligence.demandByCategory")}</CardTitle>
              <CardDescription>{t("intelligence.whatBuyersAreSourcing")}</CardDescription>
            </CardHeader>
            <CardContent>
              {demand?.categories?.length > 0 ? (
                <div className="space-y-3">
                  {demand.categories.map((cat, i) => {
                    const maxCount = demand.categories[0]?.rfq_count || 1;
                    const pct = Math.round((cat.rfq_count / maxCount) * 100);
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium capitalize">{cat.category?.replace(/_/g, ' ')}</span>
                          <span className="text-muted-foreground">{cat.rfq_count} RFQs / {cat.unique_buyers} buyers</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">{t("intelligence.noDemandDataYet")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supply Density */}
        <TabsContent value="supply" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("intelligence.byIndustry")}</CardTitle></CardHeader>
              <CardContent>
                {supply?.by_industry?.length > 0 ? (
                  <div className="space-y-2">
                    {supply.by_industry.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="capitalize">{item.industry?.replace(/_/g, ' ') || 'Unknown'}</span>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">{t("intelligence.noSupplierDataYet")}</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("intelligence.byCountry")}</CardTitle></CardHeader>
              <CardContent>
                {supply?.by_country?.length > 0 ? (
                  <div className="space-y-2">
                    {supply.by_country.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{item.country || 'Unknown'}</span>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">{t("intelligence.noSupplierDataYet")}</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("intelligence.byType")}</CardTitle></CardHeader>
              <CardContent>
                {supply?.by_company_type?.length > 0 ? (
                  <div className="space-y-2">
                    {supply.by_company_type.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="capitalize">{item.type?.replace(/_/g, ' ') || 'Unknown'}</span>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">{t("intelligence.noSupplierDataYet")}</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("intelligence.byVerification")}</CardTitle></CardHeader>
              <CardContent>
                {supply?.by_verification_tier?.length > 0 ? (
                  <div className="space-y-2">
                    {supply.by_verification_tier.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{item.tier === 0 ? 'Unverified' : item.tier === 1 ? 'Verified' : 'Premium'}</span>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">{t("intelligence.noDataYet")}</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Benchmarks */}
        <TabsContent value="benchmarks" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("intelligence.yourPerformanceVsPlatform")}</CardTitle>
              <CardDescription>{t("intelligence.seeHowYouStack")}</CardDescription>
            </CardHeader>
            <CardContent>
              {benchmarks?.comparisons ? (
                <div className="space-y-4">
                  {benchmarks.comparisons.map((comp, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded border">
                      <div>
                        <p className="font-medium text-sm">{comp.metric}</p>
                        <p className="text-xs text-muted-foreground">Platform avg: {comp.platform_avg}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{comp.yours}</p>
                        <Badge variant={comp.status === 'above' ? 'default' : 'outline'} className="text-xs">
                          {comp.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground text-center py-8">{t("intelligence.completeSomeDealsTo")}</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
